/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createEffect, createSignal } from 'davaux/client'
import type { StyleProps } from './types.js'
import { cx, pickStyleProps } from './types.js'

export interface SpotlightAction {
  id: string
  label: string
  description?: string
  group?: string
  href?: string
}

export interface SpotlightProps extends StyleProps {
  actions?: SpotlightAction[]
  placeholder?: string
  nothingFound?: string
  limit?: number
  /** Key to combine with Ctrl/Cmd. Default: 'k' */
  shortcutKey?: string
  /** CSS selector for an element that opens Spotlight on click */
  trigger?: string
  class?: string
  style?: string | Record<string, string>
  [key: string]: unknown
}

const SEARCH_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="1.125em" height="1.125em" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="6.5" cy="6.5" r="4.5"/><path d="M10 10l3.5 3.5"/></svg>'

type FlatEntry =
  | { type: 'group'; label: string }
  | { type: 'action'; action: SpotlightAction; idx: number }

function Spotlight({
  actions = [],
  placeholder = 'Search…',
  nothingFound = 'Nothing found',
  limit = 10,
  shortcutKey = 'k',
  trigger,
  class: className,
  style,
  ...rest
}: SpotlightProps) {
  const [open, setOpen] = createSignal(false)
  const [query, setQuery] = createSignal('')
  const [activeIdx, setActiveIdx] = createSignal(0)

  let inputEl: HTMLInputElement | null = null
  const { ownRest } = pickStyleProps(rest as Record<string, unknown>)

  function openSpotlight() {
    setOpen(true)
    setQuery('')
    setActiveIdx(0)
    requestAnimationFrame(() => inputEl?.focus())
  }

  createEffect(() => {
    if (typeof document === 'undefined') return
    const keyHandler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === shortcutKey) {
        e.preventDefault()
        openSpotlight()
      }
    }
    document.addEventListener('keydown', keyHandler)
    document.addEventListener('dv-spotlight-open', openSpotlight)
    if (trigger) {
      const el = document.querySelector(trigger)
      el?.addEventListener('click', openSpotlight)
    }
  })

  function close() {
    setOpen(false)
    setQuery('')
    setActiveIdx(0)
  }

  const filtered = () => {
    const q = query().toLowerCase().trim()
    const result = q
      ? actions.filter(
          (a) =>
            a.label.toLowerCase().includes(q) ||
            a.description?.toLowerCase().includes(q),
        )
      : actions
    return result.slice(0, limit)
  }

  function select(action: SpotlightAction) {
    close()
    if (action.href) {
      window.location.href = action.href
      return
    }
    document.dispatchEvent(
      new CustomEvent('dv-spotlight-select', { detail: { id: action.id }, bubbles: true }),
    )
  }

  function handleKeyDown(e: KeyboardEvent) {
    const items = filtered()
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx(Math.min(activeIdx() + 1, items.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx(Math.max(activeIdx() - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = items[activeIdx()]
      if (item) select(item)
    } else if (e.key === 'Escape') {
      close()
    }
  }

  return (
    <div
      class={cx('dv-spotlight-root', className)}
      style={style}
      {...ownRest}
    >
      <div
        class='dv-spotlight'
        data-opened={() => open() || undefined}
        onKeyDown={handleKeyDown}
        role='dialog'
        aria-modal='true'
        aria-label='Spotlight search'
      >
        <div class='dv-spotlight-overlay' onClick={close} aria-hidden='true' />
        <div class='dv-spotlight-content'>
          <div class='dv-spotlight-search-wrap'>
            <span
              class='dv-spotlight-search-icon'
              aria-hidden='true'
              dangerouslySetInnerHTML={{ __html: SEARCH_ICON }}
            />
            <input
              class='dv-spotlight-input'
              type='search'
              ref={(el: Element) => { inputEl = el as HTMLInputElement }}
              placeholder={placeholder}
              value={() => query()}
              onInput={(e: Event) => {
                setQuery((e.target as HTMLInputElement).value)
                setActiveIdx(0)
              }}
              aria-autocomplete='list'
              aria-controls='dv-spotlight-list'
            />
            <kbd class='dv-spotlight-hint'>Esc</kbd>
          </div>
          <div id='dv-spotlight-list' class='dv-spotlight-list' role='listbox'>
            {() => {
              const items = filtered()
              if (items.length === 0) {
                return <div class='dv-spotlight-empty'>{nothingFound}</div>
              }
              const entries: FlatEntry[] = []
              let globalIdx = 0
              let lastGroup: string | undefined
              for (const action of items) {
                if (action.group !== lastGroup) {
                  lastGroup = action.group
                  if (action.group) entries.push({ type: 'group', label: action.group })
                }
                entries.push({ type: 'action', action, idx: globalIdx++ })
              }
              return entries.map((entry) => {
                if (entry.type === 'group') {
                  return <div class='dv-spotlight-group'>{entry.label}</div>
                }
                const { action, idx } = entry
                return (
                  <div
                    class='dv-spotlight-item'
                    role='option'
                    aria-selected={idx === activeIdx()}
                    data-active={idx === activeIdx() || undefined}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => select(action)}
                  >
                    <span class='dv-spotlight-item-label'>{action.label}</span>
                    {action.description && (
                      <span class='dv-spotlight-item-desc'>{action.description}</span>
                    )}
                  </div>
                )
              })
            }}
          </div>
        </div>
      </div>
    </div>
  )
}

export default reactive(Spotlight)
